/**
 * iter 259 R3 — Dev-only auto-detector for overlapping floating buttons.
 *
 * Reason for existence: the AI Companion "buttons fail to show results" bug
 * was caused by two `position: fixed` elements landing in the same corner
 * with the same z-index, silently eating each other's clicks. This hook
 * scans the DOM after mount and warns when it detects that pattern, so the
 * next collision surfaces immediately in the console instead of as a
 * support ticket.
 *
 * Active ONLY when NODE_ENV === 'development' OR the hostname includes
 * 'localhost' / 'preview'. Production runtime gets a no-op.
 */
import { useEffect } from 'react';

const CORNERS = [
  { id: 'bottom-right', match: (s) => s.bottom !== 'auto' && s.right !== 'auto' && parseInt(s.bottom) < 100 && parseInt(s.right) < 100 },
  { id: 'bottom-left',  match: (s) => s.bottom !== 'auto' && s.left !== 'auto'  && parseInt(s.bottom) < 100 && parseInt(s.left)  < 100 },
  { id: 'top-right',    match: (s) => s.top    !== 'auto' && s.right !== 'auto' && parseInt(s.top)    < 100 && parseInt(s.right) < 100 },
  { id: 'top-left',     match: (s) => s.top    !== 'auto' && s.left !== 'auto'  && parseInt(s.top)    < 100 && parseInt(s.left)  < 100 }
];

const useFloatingOverlapDetector = () => {
  useEffect(() => {
    const isDev =
      process.env.NODE_ENV === 'development' ||
      window.location.hostname === 'localhost' ||
      window.location.hostname.includes('preview');
    if (!isDev) return undefined;

    // Wait a beat so React has mounted everything
    const timer = setTimeout(() => {
      const fixedEls = Array.from(document.querySelectorAll('body *')).filter((el) => {
        const s = window.getComputedStyle(el);
        return s.position === 'fixed' && parseInt(s.zIndex || '0') >= 10 && el.offsetWidth > 0 && el.offsetHeight > 0;
      });

      const byCorner = {};
      fixedEls.forEach((el) => {
        const s = window.getComputedStyle(el);
        for (const c of CORNERS) {
          if (c.match(s)) {
            byCorner[c.id] = byCorner[c.id] || [];
            byCorner[c.id].push({ el, zIndex: parseInt(s.zIndex || '0') });
            break;
          }
        }
      });

      Object.entries(byCorner).forEach(([corner, entries]) => {
        if (entries.length < 2) return;
        // Same-z collisions are the real bug — one will eat the other's clicks
        const z = entries[0].zIndex;
        const sameZ = entries.filter((e) => e.zIndex === z);
        if (sameZ.length >= 2) {
          console.warn(
            `[FloatingOverlapDetector] ${sameZ.length} floating elements share ${corner} @ z-index ${z}. ` +
            'They will likely intercept each other\u2019s clicks. Selectors:',
            sameZ.map((e) => {
              const el = e.el;
              const id = el.id ? `#${el.id}` : '';
              const cls = el.className && typeof el.className === 'string'
                ? '.' + el.className.split(' ').slice(0, 3).join('.')
                : '';
              const testid = el.getAttribute('data-testid');
              return `${el.tagName.toLowerCase()}${id}${cls}${testid ? `[data-testid="${testid}"]` : ''}`;
            })
          );
        }
      });
    }, 1500);
    return () => clearTimeout(timer);
  }, []);
};

export default useFloatingOverlapDetector;
